import { useState, useEffect } from 'react';
/* MODIFICADO: Importado useTheme para adaptar cores ao tema */
/* ANTES: Não havia import do contexto de tema */
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-hot-toast';

interface BackupFile {
  fileName: string;
  size: number;
  createdAt: string;
  status: 'success' | 'failed' | 'in_progress';
  type: 'full' | 'database' | 'uploads';
}

interface BackupConfig {
  enabled: boolean;
  schedule: string;
  retentionDays: number;
  storageType: 'local' | 's3';
}

const SCHEDULE_OPTIONS = [
  { value: '0 */6 * * *', label: 'A cada 6 horas' },
  { value: '0 */12 * * *', label: 'A cada 12 horas' },
  { value: '0 2 * * *', label: 'Diariamente às 2h' },
  { value: '0 2 * * 0', label: 'Semanalmente (Domingo às 2h)' },
  { value: '0 2 1 * *', label: 'Mensalmente (Dia 1 às 2h)' }
];

export function SystemBackup() {
  /* MODIFICADO: Hook do tema para aplicar variáveis de cor no dark mode */
  /* ANTES: Componente não consumia o tema */
  const { theme } = useTheme();
  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [config, setConfig] = useState<BackupConfig>({
    enabled: false,
    schedule: '0 2 * * *',
    retentionDays: 30,
    storageType: 'local'
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<BackupFile | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadBackups();
    loadConfig();
  }, []);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        Authorization: `Bearer ${token}`
      }
    });
  };

  const loadBackups = async () => {
    try {
      const response = await authenticatedFetch('/api/backup/system');
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      } else {
        throw new Error('Erro ao carregar backups');
      }
    } catch (error) {
      console.error('Erro ao carregar backups:', error);
      toast.error('Erro ao carregar lista de backups');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await authenticatedFetch('/api/backup/system/config');
      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          setConfig(data.config);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await authenticatedFetch('/api/backup/system', {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Backup criado com sucesso!');
        await loadBackups();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao criar backup');
      }
    } catch (error: any) {
      console.error('Erro ao criar backup:', error);
      toast.error(error.message || 'Erro ao criar backup');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      const response = await authenticatedFetch('/api/backup/system/configure', {
        method: 'POST',
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configuração salva com sucesso!');
        setShowConfigModal(false);
        await loadConfig();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao salvar configuração');
      }
    } catch (error: any) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.message || 'Erro ao salvar configuração');
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;

    setRestoring(true);
    try {
      const confirmed = window.confirm(
        '⚠️ ATENÇÃO: Esta ação irá restaurar TODO o sistema para o estado do backup selecionado. ' +
        'Todos os dados atuais serão substituídos. Esta ação é IRREVERSÍVEL. ' +
        '\n\nDeseja continuar?'
      );

      if (!confirmed) {
        setRestoring(false);
        return;
      }

      const response = await authenticatedFetch('/api/backup/system/restore', {
        method: 'POST',
        body: JSON.stringify({ fileName: selectedBackup.fileName })
      });

      if (response.ok) {
        toast.success('Backup restaurado com sucesso! Recomenda-se reiniciar o sistema.');
        setShowRestoreModal(false);
        setSelectedBackup(null);
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao restaurar backup');
      }
    } catch (error: any) {
      console.error('Erro ao restaurar backup:', error);
      toast.error(error.message || 'Erro ao restaurar backup');
    } finally {
      setRestoring(false);
    }
  };

  const handleDownloadBackup = async (fileName: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/backup/system/download/${fileName}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao fazer download do backup');
      }

      // Criar blob e fazer download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Download iniciado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao fazer download:', error);
      toast.error(error.message || 'Erro ao fazer download do backup');
    }
  };

  const handleUploadAndRestore = async () => {
    if (!uploadFile) {
      toast.error('Selecione um arquivo de backup');
      return;
    }

    // Validar extensão do arquivo
    if (!uploadFile.name.endsWith('.tar.gz')) {
      toast.error('Arquivo deve ser um backup válido (.tar.gz)');
      return;
    }

    const confirmed = window.confirm(
      '⚠️ ATENÇÃO CRÍTICA: Esta ação irá:\n\n' +
      '1. Fazer upload do arquivo de backup\n' +
      '2. SUBSTITUIR COMPLETAMENTE o banco de dados atual\n' +
      '3. SUBSTITUIR COMPLETAMENTE todos os arquivos\n' +
      '4. EXCLUIR todos os dados atuais\n\n' +
      'Esta ação é IRREVERSÍVEL e NÃO PODE SER DESFEITA!\n\n' +
      'Tem CERTEZA ABSOLUTA que deseja continuar?'
    );

    if (!confirmed) {
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('backup', uploadFile);

      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/backup/system/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Backup enviado com sucesso! Aguarde a restauração...');

        // Agora restaurar o backup que acabou de fazer upload
        const restoreResponse = await authenticatedFetch('/api/backup/system/restore', {
          method: 'POST',
          body: JSON.stringify({ fileName: data.fileName })
        });

        if (restoreResponse.ok) {
          toast.success('Sistema restaurado com sucesso! Recomenda-se reiniciar os serviços.');
          setShowUploadModal(false);
          setUploadFile(null);
          await loadBackups();
        } else {
          const error = await restoreResponse.json();
          throw new Error(error.message || 'Erro ao restaurar backup');
        }
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao enviar arquivo de backup');
      }
    } catch (error: any) {
      console.error('Erro ao fazer upload e restaurar:', error);
      toast.error(error.message || 'Erro ao fazer upload e restaurar backup');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* MODIFICADO: Card Header e Ações adaptado para tema */}
      {/* ANTES: className="bg-white" e textos com text-gray-*/}
      <div
        className="rounded-lg shadow p-6"
        style={{
          backgroundColor: 'var(--bg-card)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-color)'
        }}
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            {/* MODIFICADO: Cores do título e descrição */}
            {/* ANTES: text-gray-900 e text-gray-600 */}
            <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Backup do Sistema</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Gerencie backups completos do sistema (banco de dados + arquivos)
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowConfigModal(true)}
              /* MODIFICADO: Botão secundário usa variáveis de tema */
              /* ANTES: bg-gray-100 text-gray-700 hover:bg-gray-200 */
              className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)'
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Configurar
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Restaurar de Arquivo
            </button>
            <button
              onClick={handleCreateBackup}
              disabled={creating}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              {creating ? 'Criando...' : 'Criar Backup'}
            </button>
          </div>
        </div>

        {/* MODIFICADO: Status do Backup Automático com cores por tema */}
        {/* ANTES: bg-green-50/bg-gray-50 fixos */}
        <div
          className="px-4 py-3 rounded-lg"
          style={{
            backgroundColor:
              config.enabled
                ? (theme === 'dark' ? 'rgba(16,185,129,0.12)' : '#ecfdf5')
                : (theme === 'dark' ? 'var(--bg-secondary)' : '#f9fafb'),
            border: '1px solid var(--border-color)'
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${config.enabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Backup Automático: {config.enabled ? 'Ativo' : 'Desativado'}
                </p>
                {config.enabled && (
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Próximo backup: {SCHEDULE_OPTIONS.find(opt => opt.value === config.schedule)?.label || config.schedule}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowConfigModal(true)}
              className="text-sm font-medium"
              style={{ color: '#3b82f6' }}
            >
              {config.enabled ? 'Modificar' : 'Ativar'}
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Backups */}
      {/* MODIFICADO: Lista de Backups - cores ajustadas ao tema */}
      {/* ANTES: bg-white, border-gray-200, text-gray-* */}
      <div
        className="rounded-lg shadow"
        style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Backups Disponíveis</h3>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{backups.length} backup(s) encontrado(s)</p>
        </div>

        {backups.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="mx-auto h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: theme === 'dark' ? '#9ca3af' : '#9ca3af' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-secondary)' }}>Nenhum backup encontrado</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Clique em "Criar Backup" para começar</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
            {backups.map((backup) => (
              <div
                key={backup.fileName}
                className="p-6 transition-colors"
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#3b82f6' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{backup.fileName}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{formatFileSize(backup.size)}</span>
                          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{formatDate(backup.createdAt)}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            backup.status === 'success' ? 'bg-green-100 text-green-800' :
                            backup.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {backup.status === 'success' ? 'Sucesso' :
                             backup.status === 'failed' ? 'Falhou' : 'Em progresso'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDownloadBackup(backup.fileName)}
                      /* MODIFICADO: Botão linkado com hover de fundo compatível */
                      /* ANTES: hover:bg-blue-50 fixo */
                      className="px-3 py-2 text-sm rounded-lg flex items-center gap-2"
                      style={{ color: '#3b82f6' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(59,130,246,0.1)' : '#eff6ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </button>
                    <button
                      onClick={() => {
                        setSelectedBackup(backup);
                        setShowRestoreModal(true);
                      }}
                      className="px-3 py-2 text-sm rounded-lg flex items-center gap-2"
                      style={{ color: '#ea580c' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(234,88,12,0.08)' : '#fff7ed'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Restaurar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Configuração */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          {/* MODIFICADO: Modal de Configuração com tema */}
          {/* ANTES: bg-white, border-gray-200, text-gray-* */}
          <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-xl font-semibold text-n-slate-12">Configurar Backup Automático</h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  /* MODIFICADO: Borda do input harmonizada */
                  /* ANTES: border-gray-300 */
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
                  style={{ border: '1px solid var(--border-color)' }}
                />
                <label htmlFor="enabled" className="ml-2 block text-sm" style={{ color: 'var(--text-primary)' }}>
                  Ativar backup automático
                </label>
              </div>

              {config.enabled && (
                <>
                  <div>
                    {/* MODIFICADO: Inputs do modal com variáveis */}
                    {/* ANTES: text-gray-700 e border-gray-300 */}
                    <label htmlFor="schedule" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      Frequência
                    </label>
                    <select
                      id="schedule"
                      value={config.schedule}
                      onChange={(e) => setConfig({ ...config, schedule: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{
                        backgroundColor: 'var(--bg-card)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      {SCHEDULE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="retention" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      Manter backups por (dias)
                    </label>
                    <input
                      type="number"
                      id="retention"
                      min="1"
                      max="365"
                      value={config.retentionDays}
                      onChange={(e) => setConfig({ ...config, retentionDays: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{
                        backgroundColor: 'var(--bg-card)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)'
                      }}
                    />
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                      Backups mais antigos serão automaticamente excluídos
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ borderColor: 'var(--border-color)' }}>
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg"
                style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveConfig}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Restauração */}
      {showRestoreModal && selectedBackup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          {/* MODIFICADO: Modal de Restauração com tema */}
          {/* ANTES: bg-white/border-gray-200 */}
          <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-xl font-semibold text-n-slate-12">⚠️ Restaurar Backup</h3>
            </div>

            <div className="p-6">
              {/* MODIFICADO: Aviso com cores dependentes do tema */}
              {/* ANTES: bg-red-50 e textos fixos */}
              <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: theme === 'dark' ? 'rgba(239,68,68,0.08)' : '#fef2f2', border: `1px solid ${theme === 'dark' ? 'rgba(239,68,68,0.25)' : '#fecaca'}` }}>
                <p className="text-sm font-medium" style={{ color: theme === 'dark' ? '#fecaca' : '#991b1b' }}>ATENÇÃO: Ação Irreversível!</p>
                <p className="text-xs mt-2" style={{ color: theme === 'dark' ? '#fecaca' : '#b91c1c' }}>
                  Esta ação irá restaurar TODO o sistema para o estado do backup selecionado.
                  Todos os dados atuais (banco de dados e arquivos) serão SUBSTITUÍDOS.
                </p>
              </div>

              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Backup selecionado:</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{selectedBackup.fileName}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Criado em: {formatDate(selectedBackup.createdAt)}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Tamanho: {formatFileSize(selectedBackup.size)}
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ borderColor: 'var(--border-color)' }}>
              <button
                onClick={() => {
                  setShowRestoreModal(false);
                  setSelectedBackup(null);
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg"
                style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleRestoreBackup}
                disabled={restoring}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {restoring ? 'Restaurando...' : 'Confirmar Restauração'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Upload de Backup */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          {/* MODIFICADO: Modal de Upload com tema */}
          {/* ANTES: bg-white/border-gray-200 */}
          <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-xl font-semibold text-n-slate-12">📤 Restaurar de Arquivo</h3>
            </div>

            <div className="p-6">
              <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: theme === 'dark' ? 'rgba(251,146,60,0.08)' : '#fff7ed', border: `1px solid ${theme === 'dark' ? 'rgba(251,146,60,0.25)' : '#fed7aa'}` }}>
                <p className="text-sm font-medium" style={{ color: theme === 'dark' ? '#ffedd5' : '#9a3412' }}>⚠️ Use esta função para:</p>
                <ul className="text-xs mt-2 ml-4 space-y-1 list-disc" style={{ color: theme === 'dark' ? '#ffedd5' : '#9a3412' }}>
                  <li>Restaurar backup em um novo servidor</li>
                  <li>Migrar dados de outro ambiente</li>
                  <li>Recuperar de um backup externo</li>
                </ul>
              </div>

              <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: theme === 'dark' ? 'rgba(239,68,68,0.08)' : '#fef2f2', border: `1px solid ${theme === 'dark' ? 'rgba(239,68,68,0.25)' : '#fecaca'}` }}>
                <p className="text-sm font-medium" style={{ color: theme === 'dark' ? '#fecaca' : '#991b1b' }}>❌ ATENÇÃO:</p>
                <p className="text-xs mt-2" style={{ color: theme === 'dark' ? '#fecaca' : '#b91c1c' }}>
                  O backup enviado substituirá TODOS os dados atuais do sistema!
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="backupFile" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Selecione o arquivo de backup (.tar.gz)
                  </label>
                  <input
                    type="file"
                    id="backupFile"
                    accept=".tar.gz"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-orange-500"
                    style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  />
                  {uploadFile && (
                    <div className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>Arquivo selecionado:</p>
                      <p className="text-xs">{uploadFile.name}</p>
                      <p className="text-xs">Tamanho: {formatFileSize(uploadFile.size)}</p>
                    </div>
                  )}
                </div>

                <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    <strong>Processo:</strong>
                  </p>
                  <ol className="text-xs mt-1 ml-4 space-y-1 list-decimal" style={{ color: 'var(--text-secondary)' }}>
                    <li>O arquivo será enviado ao servidor</li>
                    <li>O banco de dados atual será substituído</li>
                    <li>Os arquivos atuais serão substituídos</li>
                    <li>O sistema será restaurado para o estado do backup</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ borderColor: 'var(--border-color)' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg"
                style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleUploadAndRestore}
                disabled={!uploadFile || uploading}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {uploading ? 'Enviando e Restaurando...' : 'Enviar e Restaurar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
